import * as React from 'react';
import * as ReactDataGrid from 'react-data-grid';
import * as ReactDataAddOn from 'react-data-grid-addons';
import uuidv1 from 'uuid/v1';
import { Button, Icon } from 'semantic-ui-react';
import  { Identifiable, DataClass } from 'src/shared/utility';
import  DataStore from 'src/stores/application/generic/datastore';
import { Row } from 'react-data-grid';
import LoadingFrame from '../LoadingFrame';
import { observer, inject } from 'mobx-react';
import { removeTypenameRecursive, ContainerProps } from 'src/shared/typemap';
import { deepEqual } from 'src/shared/utility';
import './GenericOperatableStoreDataGrid.scss';

// Datatype for the Action Button
export type ActionButton = {
    icon: string;
    callback: () => void;
};

// Datatype for Action menu
export type ActionMenu = {
    icon: string;
    actions: {
        icon: string;
        text: string;
        callback: () => void;
    }[];
};

type SortDirections = 'ASC' | 'DESC' | 'NONE';

// Properties that is used to render the datagrid
interface DataGridProps<T extends Identifiable, C extends DataClass> extends  ContainerProps {
    toolBarOptions?: {
        enableFilter?: boolean;
        enableAddRow?: boolean;
        enableDeleteRow?: boolean;
        addRowButtonText?: string;
        filterRowsButtonText?: string;
        children?: any;
    }; // toolbar options that will be used to save data

    dataStore: DataStore<T, C>;
    defaultValue?: Function | any; // 
    preProcess?: (input: any) => any;
    columns: Array<ReactDataGrid.Column & {
        comparision?: 
            ((sortOrder: SortDirections, a: any, b: any) => number) 
            | 'string-comparision' 
            | 'number-comparision'
            | 'date-comparision';
        filterFunction?: 
            ((filterString: string, value: any) => boolean)
            | 'string-search'
            | 'numeric-search';
    }>;
    lazyLoad: boolean;

    rowHeight?: number;
    headerRowHeight?: number;
    contextMenu?: React.ReactElement<any>;
    rowRenderer?: React.ReactElement<any> | React.ComponentClass<any> | React.StatelessComponent<any>;
    emptyRowsView?: React.ComponentClass<any> | React.StatelessComponent<any>;

    getCellActions?: (column: ReactDataGrid.Column, row: any) => (ActionButton | ActionMenu)[];
    getValidFilterValues?: (columnKey: string) => Array<any>;
    editRow: (row: any, isNew?: boolean) => Promise<any>;
    routing?: any;
}

interface DataGridState <T extends Identifiable, C> {
    added?: Array<C>;
    updated?: Array<C>;
    deleted?: Array<C>;
    selectedRows?: Array<C>;
    sortColumn?: string;
    sortDirection?: SortDirections;
    filters: { [key: string]: any };
    currentEdit: any;
}

@inject('routing')
@observer
export default class DataGrid < T extends Identifiable, C extends DataClass>
extends React.Component<DataGridProps<T, C>, DataGridState<T, C>> {

    get items (): C[] {
        let parsedItems = this.props.dataStore.getParsedItems;
        if (parsedItems.flatten) {
            return parsedItems.flatten();
        }
        return parsedItems as C[];
    }

    get getOrderedItem() {       
        const { sortColumn, sortDirection, added, updated } = this.state;

        let endItems = [
            ...added, 
            ...this.items
                // .filter(o => !deleted.some(p => p.typedId === o.typedId))
                .map(o => {
                    const updatedIndex = updated.findIndex(p => p.typedId === o.typedId);
                    if (updatedIndex >= 0 && updatedIndex < updated.length) {
                        return updated[updatedIndex];
                    } 
                    return o;
                })
        ];
        
        const currentSortColumnIndex = this.props.columns.findIndex(o => o.key === sortColumn);
        if (currentSortColumnIndex >= 0) {
            const currentSortColumn = this.props.columns[currentSortColumnIndex];

            if ( currentSortColumn && currentSortColumn.sortable ) {
                if ( currentSortColumn.comparision ) {
                    
                    switch (currentSortColumn.comparision) {
                        
                        case 'number-comparision':
                            if (sortDirection === 'DESC') {
                                endItems = endItems.sort((a: any, b: any) => 
                                    b[sortColumn] ?
                                    a[sortColumn] ? 
                                    b[sortColumn] - a[sortColumn] 
                                    : -1 : -1
                                );
                            } else {
                                endItems = endItems.sort((a: any, b: any) => 
                                    b[sortColumn] ?
                                    a[sortColumn] ? 
                                    a[sortColumn] - b[sortColumn] 
                                    : -1 : -1
                                );
                            }
                            break;
                        
                        case 'string-comparision':
                        case 'date-comparision':
                            endItems = endItems.sort(this.defaultComparision(sortDirection, sortColumn));
                            break;
                        default:
                            endItems = endItems.sort(
                                currentSortColumn.comparision.bind(this, this.state.sortDirection)
                            );
                            break;
                    }
                } else {
                    endItems = endItems.sort(this.defaultComparision(sortDirection, sortColumn));
                }
            }
        }

        endItems = ReactDataAddOn.Data.Selectors.getRows({
            filters: this.state.filters,
            rows: endItems,
        }) as any;

        return endItems;
    }

    constructor(props: DataGridProps<T, C>) {
        super(props);
        
        this.state = {
            added: [],
            updated: [],
            deleted: [],
            selectedRows: [],
            filters: {},
            currentEdit: undefined
        };
    }

    callEdit(obj: any, isNew: boolean = false) {
        this.setRoutingEdit(obj.typedId);
        return new Promise<any>((res, rej) => {
            this.setState(
                {
                    currentEdit: obj
                },
                (() => {        
                    this.props.editRow(this.state.currentEdit, isNew)
                    .then((...dat) => {
                        this.unSetEdit();
                        res(...dat);
                    })
                    .catch((...err) => {
                        this.unSetEdit();
                        rej(...err);
                    });   
                }).bind(this)
            );
        });
    }

    setRoutingEdit(newId: string) {
        const { match } = this.props as ContainerProps;
        
        let path = match.path;
        if (/\:currentId\?/.test(path)) {
            path = path.split('/:currentId?/')[0];
        } 
        if (path[path.length - 1] !== '/') {
            newId = `/${newId}`;
        } 
        path += newId;

        if (
            this.props.location.pathname.toLowerCase() !== path.toLowerCase()
        ) {
            this.props.routing.history.push(path.toLowerCase());
        }
        
    }
    unSetEdit() {
        if (this.props.routing.history.length > 0) {
            this.props.routing.goBack();
        } else {
            const { match } = this.props as ContainerProps;
            let paths = match.url.split(match.params.currentId);
            paths.pop();
            let path = paths.join(match.params.currentId);
            this.props.routing.history.push(path);
        }
    }

    syncCurrentEdit() {
      let { match } = this.props as ContainerProps;
      if (match.params.currentId) {
        let currentEditTyped = match.params.currentId;
        const _dataStore = this.props.dataStore;
        const parsedItems: C[] = _dataStore.getParsedItems as C[];

        if (
            (
                !this.state.currentEdit ||
                this.state.currentEdit.typedId !== currentEditTyped
            ) && ! _dataStore.loading && !_dataStore.isLoadingMore && parsedItems.length > 0
        ) {
            const itemIndex  = parsedItems.findIndex(item => item.typedId === currentEditTyped);
            const addRow = this.addUpdateRow.bind(this);

            if (itemIndex >= 0 && itemIndex < parsedItems.length) {
                this.callEdit(parsedItems[itemIndex], false)
                .then((newRow) => {
                    addRow(newRow.typedId, newRow);
                })
                .catch((err) => {
                    if (
                        typeof err === 'undefined'
                        || err.isNormal
                    ) {
                        console.warn(err);
                    } else {
                        throw err;
                    }
                });    
            }//  else if (
            //     this.state.added.every(o => o.typedId !== currentEditTyped) &&
            //     this.state.updated.every(o => o.typedId !== currentEditTyped)
            // ) {
            //     this.unSetEdit();
            // }
        }
      }
    }
    
    componentDidMount() {
        this.syncCurrentEdit();
    }    

    shouldComponentUpdate ( props: DataGridProps<T, C>, state: DataGridState <T, C> ) {
        const flattable = props.dataStore.getParsedItems;
        const items = flattable.flatten ? flattable.flatten() : flattable;
        let _added = state.added.filter (
            _add => ! items.some (
                item => item.typedId === _add.typedId
            )
        );

        let _deleted = state.deleted.filter (
            del => items.some(
                item => item.typedId === del.typedId
            )
        );

        let _updated = state.updated.filter (
            _update => !items.some (
                (_item) => { 
                    let item = _item && _item.parseAbsolute ? 
                        _item.parseAbsolute() : _item;
                    let update = _update && _update.parseAbsolute ?
                        _update.parseAbsolute() : _update;
                    
                    let ret = deepEqual(
                        removeTypenameRecursive(item),
                        removeTypenameRecursive(update)
                    );
                    return ret;
                }
            )
        );
        const newState = {
            added: _added,
            updated: _updated,
            deleted: _deleted
        };
        if (
            this.state.added.length !== newState.added.length ||
            this.state.updated.length !== newState.updated.length ||
            this.state.deleted.length !== newState.deleted.length
        ) {
            this.setState(newState);
            return false;
        } else {
            return true;
        }
    }

    componentDidUpdate( props: DataGridProps<T, C>, state: DataGridState <T, C> ) {
        // making sure that component always follow the lifecycle and update add
        this.syncCurrentEdit();
        this.shouldComponentUpdate(props, state);
    }
    
    deleteSelected() {
        if (window.confirm('Are you sure you want to delete these items?')) { 

            this.state.selectedRows.forEach((i) => {
                if (!this.state.deleted.some(deleted => deleted.typedId === i.typedId)) {
                    this.remove(i, false);
                }
            });
            this.setState({
                deleted: [...this.state.deleted, ...this.state.selectedRows],
                selectedRows: []
            });
        }
    }
  
    getToolBarChilds () {
      const { dataStore } = this.props;

      return (
        <Button.Group key="saver">
            {(
                this.props.toolBarOptions
                && this.props.toolBarOptions.enableAddRow
                && dataStore.canCreate
            ) ?
                <Button 
                    primary 
                    key="addNew" 
                    onClick={this.handleAddRow.bind(this)} 
                > 
                    <Icon name="add"/> Add New
                </Button>
                : null
            }
            {(
                dataStore.canDelete && 
                this.state.selectedRows &&
                this.state.selectedRows.length &&
                this.state.selectedRows.length > 0
            ) ?
                [
                    <Button.Or key="deleterOr" />,
                    <Button 
                        key="deleter" 
                        onClick={this.deleteSelected.bind(this)} 
                        negative
                    >
                        <Icon name="trash"/> Delete
                    </Button>
                ]
                : null
            }
        </Button.Group>
      );
    }
    
    render() {
        const me  = this;
        const { added, updated, deleted } = me.state;
        const preProcess = typeof this.props.preProcess === 'function' ?
            this.props.preProcess : input => input;
        const orderedItem = preProcess(this.getOrderedItem);
        let rowsCount = this.getOrderedItem.length;
        let rowGetter = (index) => orderedItem[index];
        if ( this.props.dataStore.loading ) {
            // setTimeout(this.forceUpdate.bind(this), 1000); // make sure that we recheck the update
            return (
                <LoadingFrame type={'beat'}/>
            );
        } else {
            return (
                <ReactDataGrid 
                    rowsCount={rowsCount}
                    rowGetter={rowGetter}
                    cellNavigationMode="changeRow"
                    enableRowSelect="multi"
                    getValidFilterValues={this.getValidFilterValues.bind(this)}
                    toolbar={<ReactDataAddOn.Toolbar  
                        {...this.props.toolBarOptions}
                        enableAddRow={false}
                    > 
                        {this.getToolBarChilds()}
                    </ReactDataAddOn.Toolbar>}
                    headerRowHeight={35}
                    {...this.props}
                    {...this.getBindables()}
                    rowSelection={{
                        showCheckbox: true,
                        enableShiftSelect: true,
                        onRowsSelected: this.onRowsSelected.bind(this),
                        onRowsDeselected: this.onRowsDeselected.bind(this),
                        selectBy: {
                          keys: { rowKey: 'typedId', values: this.state.selectedRows.map(o => o.typedId)}
                        }
                      }} 
                    rowRenderer={BoundRowRenderer({added, updated, deleted })}
                />
            );
        }
    }  

    private getBindables() {
        const me = this;
        let { 
            onRowUpdated, 
            onGridRowsUpdated, 
            onGridSort,
            onAddFilter,
            onClearFilters,
            onRowSelect,
            onRowClick
        } = me;

        let bindables = { 
            onRowUpdated, 
            onGridRowsUpdated, 
            onGridSort,
            onAddFilter,
            onClearFilters,
            onRowSelect,
            onRowClick
        };
        
        Object.keys(bindables).forEach((key) => {
            bindables[key] = bindables[key].bind(me);
        });
        return bindables;
    }

    private onRowsSelected(rows: any) {
        const { selectedRows } = this.state;
        let _selectedRows = [
            ...selectedRows,
            ... rows.map(row => row.row).filter( 
                row => !selectedRows.some(selectedRow => row.typedId === selectedRow.typedId)
            )  // removing duplicates
        ];

        this.setState({
            selectedRows: _selectedRows
        });
    }
    
    private onRowsDeselected = (rowDetails) => {
        const { selectedRows } = this.state;
        const rows = rowDetails.map(rowDetail => rowDetail.row);

        let _selectedRows = selectedRows.filter(
            selectedRow => !rows.some(row => row.typedId === selectedRow.typedId)
        );

        this.setState({
            selectedRows: _selectedRows
        });
    }

    private getValidFilterValues(columnId: string) {
        let values = this.items.map(r => r[columnId]);
        return values.filter((item, i, a) => { return i === a.indexOf(item); });
    }
 
    private defaultComparision(sortDirection: 'ASC' | 'DESC' | 'NONE', sortColumn: string) {
        
        if (sortDirection === 'DESC') {
            return (
                (a: any, b: any) => 
                    a[sortColumn] < b[sortColumn] ? 1
                    : a[sortColumn] > b[sortColumn] ? -1
                    : 0
            );
        } else {
            return (
                (a: any, b: any) => 
                    a[sortColumn] < b[sortColumn] ? -1
                    : a[sortColumn] > b[sortColumn] ? 1
                    : 0
            );
        }
    }

    private handleAddRow() {
        const addRow = this.addUpdateRow.bind(this);
        const me = this;
        const addVal = this.props.defaultValue ? 
        (
            typeof (this.props.defaultValue) === 'function' ?
            this.props.defaultValue()
            : this.props.defaultValue
        ) : {
            typedId: uuidv1()
        };
        
        this.callEdit(addVal, true)
        .then((payload) => {
            addRow(payload.typedId, payload);
        })
        .catch((err) => {
            if (
                typeof err === 'undefined'
                || err.isNormal
            ) {
                let { added } = me.state;
                let addedIndex = added.findIndex(o => o.typedId === err.typedId);

                if (addedIndex >= 0) {
                    added = [
                        ...added.slice(0, addedIndex),
                        ...added.slice(addedIndex + 1, added.length),
                    ];
                }
            } else {
                throw err;
            }
        });
    }

    private addUpdateRow(typedId: string, update: any) {
        const items = this.items;
        if (
            items.some(o => o.typedId === typedId)
        ) {
            this.update(update);
        } else {
            this.add(update);
        }
        
    }

    private add(newObj: C) {
        if (this.props.dataStore.canCreate) {
            this.setState({
                added: [newObj, ...this.state.added]
            });
            this.props.dataStore.create(newObj);
        }
    }

    private update(updatedObj: C) {
        if (this.props.dataStore.canUpdate) {
            this.setState({
                updated: [ updatedObj, ...this.state.updated]
            });
            this.props.dataStore.update(updatedObj);
        }
    }

    private remove(removeObj: C, updateState: boolean = true) {
        if (this.props.dataStore.canDelete) {
            if (updateState) {
                this.setState({
                    deleted: [removeObj, ...this.state.updated]
                });
            }
            this.props.dataStore.delete(removeObj);
        }
    }

    private onRowUpdated(e: ReactDataGrid.RowUpdateEvent) {
        let item = this.items[e.rowIdx];
        
        let id = typeof item === 'undefined' ? 
            e.cellKey === 'id' ?
            e.updated[e.cellKey]
            : uuidv1() 
            : item.typedId;

        this.addUpdateRow(id, e.updated);
    }

    private onGridRowsUpdated(e: ReactDataGrid.GridRowsUpdatedEvent) {
        for (let counter = e.fromRow; counter <= e.toRow; counter++) {
            this.onRowUpdated({
                cellKey: e.cellKey,
                rowIdx: counter,
                updated: e.updated,
                key: ''
            });
        }
    }

    private onGridSort (sortColumn: string, sortDirection: 'ASC' | 'DESC' | 'NONE') {
        this.setState({ sortColumn, sortDirection});
    }

    private onAddFilter (filter: any) {
        let endObject = {
            filters: {
                ...this.state.filters,
                [filter.column.key]: filter
            }
        };

        if (
            typeof filter.filterTerm === 'undefined' 
            || filter.filterTerm === ''
        ) {
            delete endObject.filters[filter.column.key];
        }

        this.setState(endObject);
    }

    private onClearFilters () {
        this.setState({
            filters: {}
        });
    }

    private onRowSelect (rows: Array<any>) {
        // Not Implemented
    }

    private onRowClick(rowIdx: number, row: any) {
        if (!this.props.editRow || ! row) {
            return;
        }
        const me = this;
        const  { added, updated, deleted } = me.state;
        if (
            added.some(o => o.typedId === row.typedId) ||
            updated.some(o => o.typedId === row.typedId) ||
            deleted.some(o => o.typedId === row.typedId)
        ) {
            return;
        }
        const addUpdateRow = me.addUpdateRow.bind(me);

        this.callEdit(row, false)
        .then((newRow) => {
            addUpdateRow(newRow.typedId, newRow);
        })
        .catch((err) => {
            if (
                typeof err === 'undefined'
                || err.isNormal
            ) {
                console.warn(err);
            } else {
                throw err;
            }
        });     
    }
    
}

function BoundRowRenderer(bindProps: any) {
    return  class RowRenderer extends React.Component<any, any> {
        row: any;
        setScrollLeft = (scrollBy) => {
          // if you want freeze columns to work, you need to make sure you implement this as apass through
          this.row.setScrollLeft(scrollBy);
        }
      
        getRowClass = () => {
            const {added, updated, deleted} = bindProps;
            if (
                added.some(o => 
                    o.typedId === this.props.row.typedId
                )
            ) {
                return 'added';
            } else if (
                updated.some(o => 
                    o.typedId === this.props.row.typedId
                )
            ) {
                return 'updated';
            } else if (
                deleted.some(o => 
                    o.typedId === this.props.row.typedId
                )
            ) {
                return 'deleted';
            } else {
                return '';
            }
        }
      
        render() {
          // here we are just changing the style
          // but we could replace this with anything we liked, cards, images, etc
          // usually though it will just be a matter of wrapping a div, and then calling back through to the grid
          return (<div className={this.getRowClass()}><Row ref={node => this.row = node} {...this.props}/></div>);
        }
    };
}



// WEBPACK FOOTER //
// ./src/components/generic/GenericOperatableStoreDataGrid.tsx